import { useState, useEffect, useCallback, useRef } from "react";
import styles from "./ChatHistory.module.css";
import {
    fetchConversations,
    fetchConversationDetail,
    submitFeedback,
    searchConversations,
    getExportUrl,
    ConversationSummary,
    ConversationMessage,
    SearchResult
} from "../../api/userApi";

interface Props {
    onClose: () => void;
    onLoadConversation?: (messages: ConversationMessage[], sessionId: string) => void;
}

export const ChatHistory = ({ onClose, onLoadConversation }: Props) => {
    const [conversations, setConversations] = useState<ConversationSummary[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedSession, setSelectedSession] = useState<string | null>(null);
    const [messages, setMessages] = useState<ConversationMessage[]>([]);
    const [loadingDetail, setLoadingDetail] = useState(false);

    // Search state
    const [searchQuery, setSearchQuery] = useState("");
    const [searchResults, setSearchResults] = useState<SearchResult[] | null>(null);
    const [searching, setSearching] = useState(false);
    const searchTimeout = useRef<ReturnType<typeof setTimeout>>();

    useEffect(() => {
        loadConversations();
    }, []);

    const loadConversations = async () => {
        setLoading(true);
        try {
            const data = await fetchConversations();
            setConversations(data);
        } catch {
            // silently fail
        } finally {
            setLoading(false);
        }
    };

    const openConversation = async (sessionId: string) => {
        setSelectedSession(sessionId);
        setLoadingDetail(true);
        try {
            const data = await fetchConversationDetail(sessionId);
            setMessages(data);
        } catch {
            setMessages([]);
        } finally {
            setLoadingDetail(false);
        }
    };

    const handleFeedback = async (msgId: string, feedback: "positive" | "negative") => {
        try {
            await submitFeedback(msgId, feedback);
            // Update local state
            setMessages(prev => prev.map(m => (m.id === msgId ? { ...m, feedback } : m)));
        } catch {
            // ignore
        }
    };

    const handleSearch = useCallback(
        (query: string) => {
            setSearchQuery(query);
            if (searchTimeout.current) clearTimeout(searchTimeout.current);
            if (!query.trim() || query.trim().length < 2) {
                setSearchResults(null);
                return;
            }
            searchTimeout.current = setTimeout(async () => {
                setSearching(true);
                try {
                    const results = await searchConversations(query);
                    setSearchResults(results);
                } catch {
                    setSearchResults([]);
                } finally {
                    setSearching(false);
                }
            }, 400);
        },
        []
    );

    const formatDate = (ts: string) => {
        const d = new Date(ts);
        const now = new Date();
        const diffMs = now.getTime() - d.getTime();
        const diffDays = Math.floor(diffMs / 86400000);
        if (diffDays === 0) return "Today";
        if (diffDays === 1) return "Yesterday";
        if (diffDays < 7) return `${diffDays} days ago`;
        return d.toLocaleDateString();
    };

    const formatTime = (ts: string) => {
        return new Date(ts).toLocaleString();
    };

    // Click on overlay bg closes panel
    const handleOverlayClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) onClose();
    };

    // Detail view for a selected conversation
    if (selectedSession) {
        return (
            <div className={styles.overlay} onClick={handleOverlayClick}>
                <div className={styles.sidebar}>
                    <div className={styles.header}>
                        <h2>Chat History</h2>
                        <button className={styles.closeButton} onClick={onClose} aria-label="Close">
                            ✕
                        </button>
                    </div>
                    <div className={styles.detailView}>
                        <div className={styles.detailHeader}>
                            <button className={styles.backButton} onClick={() => setSelectedSession(null)} aria-label="Back">
                                ←
                            </button>
                            <span className={styles.detailTitle}>
                                {messages.length > 0 ? messages[0].question : "Conversation"}
                            </span>
                            <a
                                className={styles.exportButton}
                                href={getExportUrl(selectedSession, "text")}
                                download
                                title="Export as text"
                            >
                                &#x2B07; Export
                            </a>
                        </div>
                        <div className={styles.messages}>
                            {loadingDetail ? (
                                <div className={styles.loading}>Loading...</div>
                            ) : messages.length === 0 ? (
                                <div className={styles.empty}>No messages found</div>
                            ) : (
                                messages.map(m => (
                                    <div key={m.id} className={styles.messageBlock}>
                                        <div className={styles.questionBubble}>{m.question}</div>
                                        <div className={styles.answerBubble}>{m.answer}</div>
                                        <div className={styles.messageTime}>{formatTime(m.timestamp)}</div>
                                        <div className={styles.feedbackRow}>
                                            <button
                                                className={m.feedback === "positive" ? styles.feedbackActive : styles.feedbackButton}
                                                onClick={() => handleFeedback(m.id, "positive")}
                                                title="Helpful"
                                            >
                                                👍
                                            </button>
                                            <button
                                                className={m.feedback === "negative" ? styles.feedbackActive : styles.feedbackButton}
                                                onClick={() => handleFeedback(m.id, "negative")}
                                                title="Not helpful"
                                            >
                                                👎
                                            </button>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                        {onLoadConversation && messages.length > 0 && (
                            <div style={{ padding: "12px 20px", borderTop: "1px solid #eee" }}>
                                <button
                                    className={styles.exportButton}
                                    style={{ width: "100%", padding: "8px", fontSize: "0.9rem" }}
                                    onClick={() => {
                                        onLoadConversation(messages, selectedSession);
                                        onClose();
                                    }}
                                >
                                    Continue this conversation
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    // List view
    return (
        <div className={styles.overlay} onClick={handleOverlayClick}>
            <div className={styles.sidebar}>
                <div className={styles.header}>
                    <h2>Chat History</h2>
                    <button className={styles.closeButton} onClick={onClose} aria-label="Close">
                        ✕
                    </button>
                </div>
                <div className={styles.searchContainer}>
                    <input
                        className={styles.searchInput}
                        type="text"
                        placeholder="Search past conversations..."
                        value={searchQuery}
                        onChange={e => handleSearch(e.target.value)}
                        autoFocus
                    />
                </div>
                <div className={styles.list}>
                    {searching && <div className={styles.loading}>Searching...</div>}

                    {/* Search results */}
                    {searchResults !== null && !searching && (
                        <>
                            {searchResults.length === 0 ? (
                                <div className={styles.empty}>No results found</div>
                            ) : (
                                searchResults.map(r => (
                                    <button
                                        key={r.id}
                                        className={styles.searchResult}
                                        onClick={() => openConversation(r.session_id)}
                                    >
                                        <div className={styles.conversationQuestion}>{r.question}</div>
                                        <div className={styles.searchHighlight}>{r.answer}</div>
                                        <div className={styles.conversationMeta}>
                                            <span>{formatDate(r.timestamp)}</span>
                                        </div>
                                    </button>
                                ))
                            )}
                        </>
                    )}

                    {/* Conversation list */}
                    {searchResults === null && !searching && (
                        <>
                            {loading ? (
                                <div className={styles.loading}>Loading...</div>
                            ) : conversations.length === 0 ? (
                                <div className={styles.empty}>No conversations yet</div>
                            ) : (
                                conversations.map(c => (
                                    <button
                                        key={c.session_id}
                                        className={styles.conversationItem}
                                        onClick={() => openConversation(c.session_id)}
                                    >
                                        <div className={styles.conversationQuestion}>
                                            {c.first_question || "Untitled conversation"}
                                        </div>
                                        <div className={styles.conversationMeta}>
                                            <span>{formatDate(c.last_timestamp)}</span>
                                            <span>{c.message_count} message{c.message_count !== 1 ? "s" : ""}</span>
                                        </div>
                                    </button>
                                ))
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};
